import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Button } from "../../../design/button";
import { Input } from "../../../design/input";
import { Select } from "../../../design/select";
import {
  Fieldset,
  Field,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../design/fieldset";
import { ParticipantService } from "../../../services/ParticipantService";
import { EmploymentInfo } from "../../../models/EmploymentInfo";
import { useParticipant } from "../../../contexts/ParticipantContext";

interface Props {
  participantId: string;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  employmentInfo?: EmploymentInfo;
  onSaved?: () => void;
}

const EmploymentInfoDialog: React.FC<Props> = ({
  participantId,
  isOpen,
  setIsOpen,
  employmentInfo,
  onSaved,
}) => {
  const { createEmployment, updateEmployment } = useParticipant();
  const [employer, setEmployer] = useState("");
  const [occupation, setOccupation] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [hoursPerWeek, setHoursPerWeek] = useState<number | "">("");
  const [grossPay, setGrossPay] = useState<number | "">("");
  const [grossPayFrequency, setGrossPayFrequency] = useState<
    "hourly" | "weekly" | "monthly" | ""
  >("");

  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{
    employer?: string;
    occupation?: string;
    grossPayFrequency?: string;
  }>({});

  useEffect(() => {
    if (isOpen && employmentInfo) {
      setEmployer(employmentInfo.employer || "");
      setOccupation(employmentInfo.occupation || "");
      setPhone(employmentInfo.phone || "");
      setAddress(employmentInfo.address || "");
      setStartDate(
        employmentInfo.startDate
          ? new Date(employmentInfo.startDate).toISOString().split("T")[0]
          : "",
      );
      setEndDate(
        employmentInfo.endDate
          ? new Date(employmentInfo.endDate).toISOString().split("T")[0]
          : "",
      );
      setHoursPerWeek(employmentInfo.hoursPerWeek ?? "");
      setGrossPay(employmentInfo.grossPay ?? "");
      setGrossPayFrequency(employmentInfo.grossPayFrequency ?? "");
    } else if (isOpen) {
      setEmployer("");
      setOccupation("");
      setPhone("");
      setAddress("");
      setStartDate("");
      setEndDate("");
      setHoursPerWeek("");
      setGrossPay("");
      setGrossPayFrequency("");
      setErrors({});
    }
  }, [isOpen, employmentInfo]);

  const validate = () => {
    const newErrors: typeof errors = {};
    if (!employer.trim()) newErrors.employer = "Employer is required.";
    if (!occupation.trim()) newErrors.occupation = "Occupation is required.";
    if (!grossPayFrequency)
      newErrors.grossPayFrequency = "Select a pay frequency.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);

    try {
      const payload: Partial<EmploymentInfo> = {
        employer,
        occupation,
        phone,
        address,
        startDate: startDate ? new Date(startDate) : undefined,
        endDate: endDate ? new Date(endDate) : undefined,
        hoursPerWeek:
          typeof hoursPerWeek === "number" ? hoursPerWeek : undefined,
        grossPay: typeof grossPay === "number" ? grossPay : undefined,
        grossPayFrequency: grossPayFrequency || undefined,
      };
      if (employmentInfo) {
        await updateEmployment(participantId, employmentInfo._id, payload);
      } else {
        await createEmployment(participantId, payload);
      }
      onSaved?.();
      setIsOpen(false);
    } catch (err: any) {
      console.error("Failed to save employment info", err);
      setErrors({ employer: "Something went wrong. Please try again." });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
      <DialogTitle>
        {employmentInfo ? "Edit Employment Info" : "Add Employment Info"}
      </DialogTitle>
      <DialogBody>
        <Fieldset disabled={isSaving}>
          <FieldGroup>
            <Field>
              <Label>Employer</Label>
              <Input
                value={employer}
                onChange={(e) => setEmployer(e.target.value)}
                placeholder="e.g. ABC Corp"
              />
              {errors.employer && (
                <ErrorMessage>{errors.employer}</ErrorMessage>
              )}
            </Field>

            <Field>
              <Label>Occupation</Label>
              <Input
                value={occupation}
                onChange={(e) => setOccupation(e.target.value)}
                placeholder="e.g. Software Engineer"
              />
              {errors.occupation && (
                <ErrorMessage>{errors.occupation}</ErrorMessage>
              )}
            </Field>

            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
              <Field>
                <Label>Phone</Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g. (123) 456-7890"
                />
              </Field>

              <Field>
                <Label>Address</Label>
                <Input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="e.g. 123 Main St, City, State"
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
              <Field>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Field>

              <Field>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Field>
            </div>

            <Field>
              <Label>Hours Per Week</Label>
              <Input
                type="number"
                placeholder="e.g. 40"
                value={hoursPerWeek}
                onChange={(e) =>
                  setHoursPerWeek(e.target.value ? Number(e.target.value) : "")
                }
              />
            </Field>

            <Field>
              <Label>Gross Pay</Label>
              <Input
                type="number"
                placeholder="e.g. 50000"
                value={grossPay}
                onChange={(e) =>
                  setGrossPay(e.target.value ? Number(e.target.value) : "")
                }
              />
            </Field>

            <Field>
              <Label>Pay Frequency</Label>
              <Select
                value={grossPayFrequency}
                onChange={(e) =>
                  setGrossPayFrequency(
                    e.target.value as "hourly" | "weekly" | "monthly" | "",
                  )
                }
              >
                <option value="">Select</option>
                <option value="hourly">Hourly</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </Select>
              {errors.grossPayFrequency && (
                <ErrorMessage>{errors.grossPayFrequency}</ErrorMessage>
              )}
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>

      <DialogActions>
        <Button plain onClick={() => setIsOpen(false)}>
          Cancel
        </Button>
        <Button disabled={isSaving} onClick={handleSave}>
          {isSaving ? "Saving..." : employmentInfo ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmploymentInfoDialog;
